import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  BuildingIcon,
  FileTextIcon,
  MessageCircleIcon,
  TextAlignStartIcon,
  ZapIcon,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import { ThreadFragment } from "@/__generated__/graphql";
import { useNodeLayoutStore } from "@/components/organisms/Node/Node.hooks";
import { buildPendingNodes } from "@/components/organisms/PendingSession/PendingSession.hooks";
import { PendingChangesList } from "@/components/organisms/PendingSession/PendingSessionChangesPanel";
import { Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { ObjectType } from "@/objects/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

const SOURCE_OPTIONS = [
  { key: "user", value: "User" },
  { key: "system", value: "System" },
  { key: "rule", value: "Rule" },
];

/**
 * Everything this thread has changed or still proposes, one openable row per
 * record — the rows of its pending changes tray, across every run rather than
 * only the open one. Rejected changes are not listed: nothing of them remains.
 */
function ThreadChangesTab() {
  const store = useNodeLayoutStore();
  const threadId = store(
    useShallow(
      (state) => (state.fieldValues as ThreadFragment | undefined)?.id,
    ),
  );
  const { data, loading } = useQuery(Queries.THREAD_CHANGES_QUERY, {
    variables: { id: threadId ?? "" },
    skip: !threadId,
    fetchPolicy: "cache-and-network",
  });
  const thread = data?.node?.__typename === "ThreadObject" ? data.node : null;

  if (!thread) {
    return (
      <div className="p-2 flex flex-col gap-2">
        {loading || !threadId
          ? [0, 1, 2].map((row) => (
              <Skeleton
                key={row}
                variant="rectangular"
                height={32}
                className="rounded-sm"
              />
            ))
          : null}
      </div>
    );
  }

  if (thread.changes.length === 0) {
    return (
      <Typography
        level="body-sm"
        className="p-4 text-muted-foreground/80 text-center"
      >
        This thread hasn't changed anything.
      </Typography>
    );
  }

  return (
    <div data-testid="thread-changes" className="overflow-y-auto">
      <PendingChangesList pendingNodes={buildPendingNodes(thread.changes)} />
    </div>
  );
}

const object: ObjectType<ThreadFragment> = {
  humanName: "thread",
  inputKey: "thread",
  typename: "ThreadObject",
  ReactIcon: MessageCircleIcon,
  // No ``titleFieldKey``: a thread's name is not edited from its title bar,
  // and naming one keeps it out of Overview's rows.
  getTitle: (data) => {
    return data?.name;
  },
  getEntityId: () => {
    return null;
  },
  isEntity: false,
  getReferenceValueOptions: () => {
    return {
      query: gql`
        query ThreadList {
          threads {
            data {
              id
              identifier
              name
            }
          }
        }
      `,
      prepareOptions: (data: any) => data?.threads?.data || [],
      onRenderOption: (thread: ThreadFragment) => {
        return thread.name || thread.identifier;
      },
    };
  },
  // Changes first: what a thread did is what its panel is opened to see.
  groups: [
    {
      id: "changes",
      label: "Changes",
      component: ThreadChangesTab,
      flush: true,
    },
    { id: "overview", label: "Overview" },
  ],
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name ?? "",
      updateProps: { isEditable: false, isVisible: (data) => !!data?.name },
      summaryProps: { isVisible: (data) => !!data?.name },
    },
    {
      objectKey: "source",
      label: "Source",
      type: "enum",
      ReactIcon: ZapIcon,
      asString: (data) => data?.source ?? "",
      getValue: (data) => data?.source ?? "",
      getValueOptions: () => ({
        enum: SOURCE_OPTIONS,
        getColors: (mode) => ({
          user: EnumColors.GREY_1[mode],
          system: EnumColors.BLUE[mode],
          rule: EnumColors.PURPLE[mode],
        }),
      }),
      updateProps: { isEditable: false, isVisible: (data) => !!data?.source },
      summaryProps: { isVisible: (data) => !!data?.source },
    },
    {
      objectKey: "scopedEntities",
      label: "Entities",
      type: "references",
      ReactIcon: BuildingIcon,
      asString: (data) =>
        (data?.scopedEntities ?? []).map((entity) => entity.name).join(", "),
      getValue: (data) =>
        (data?.scopedEntities ?? []).map((entity) => ({
          __typename: "EntityObject",
          id: entity.id,
          identifier: entity.identifier,
          name: entity.name,
        })),
      updateProps: {
        isEditable: false,
        isVisible: (data) => (data?.scopedEntities?.length ?? 0) > 0,
      },
      summaryProps: {
        isVisible: (data) => (data?.scopedEntities?.length ?? 0) > 0,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: FileTextIcon,
      asString: (data) => data?.description ?? "",
      getValue: (data) => data?.description ?? "",
      updateProps: {
        isEditable: false,
        isVisible: (data) => !!data?.description,
      },
      summaryProps: { isVisible: (data) => !!data?.description },
    },
  ],
};

export { object as ThreadObject };
